import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SseClient } from 'ngx-sse-client';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';

@Injectable({ providedIn: 'root' })
export class ResearchService {

  private progressSubject = new Subject<{ data: any; progress: number }>();
  private leftSideBarSubject = new Subject<boolean>();
  leftSideBarEvent$ = this.leftSideBarSubject.asObservable();
  sseSubscription!: Subscription;

  private _projectsList: any[] = [];
  private _projects$ = new BehaviorSubject<any[]>([]);
  projects$ = this._projects$.asObservable();
  loading = false;

  constructor(
    private http: HttpClient,
    private sseClient: SseClient,
    private authService: AuthService
  ) {}

  getProgressSubject() {
    return this.progressSubject.asObservable();
  }

  getDashboard() {
    return this.http.get<any>(`${environment.api}research/dashboard`);
  }

  listProjects() {
    return this.http.get<any>(`${environment.api}research`);
  }

  createProject(data: any) {
    return this.http.post<any>(`${environment.api}research`, data);
  }

  deleteProject(projectId: number) {
    return this.http.delete<any>(`${environment.api}research/${projectId}`);
  }

  updateProject(projectId: number, data: any) {
    return this.http.patch<any>(`${environment.api}research/${projectId}`, data);
  }

  setLeftSideBar(show: boolean) {
    this.leftSideBarSubject.next(show);
  }

  async getProjects(): Promise<void> {
    return firstValueFrom(this.listProjects()).then(data => {
      const raw = data?.projectTree ?? data?.projects ?? data;
      this._projectsList = Array.isArray(raw) ? raw : [];
      this._projects$.next(this._projectsList);
      this.loading = false;
    }).catch(() => { this.loading = false; });
  }

  getProjectsList(): any[] {
    return this._projectsList;
  }

  refreshProjects(): void {
    this.getProjects();
  }

  getProject(projectId: number) {
    return this.http.get<any>(`${environment.api}research/${projectId}`);
  }

  getSession(projectId: number, sessionId: number) {
    return this.http.get<any>(`${environment.api}research/${projectId}/sessions/${sessionId}`);
  }

  deleteSession(projectId: number, sessionId: number) {
    return this.http.delete<any>(`${environment.api}research/${projectId}/sessions/${sessionId}`);
  }

  getSessionData(projectId: number, sessionId: number) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authService.token}`,
      'client_id': this.authService.clientID
    });
    const url = `${environment.api}research/${projectId}/sessions/${sessionId}/data`;
    this.sseSubscription = this.sseClient
      .stream(url, { keepAlive: false, reconnectionDelay: 0, responseType: 'event' }, { headers, withCredentials: true }, 'GET')
      .subscribe((event) => {
        const messageEvent = event as MessageEvent;
        if (event.type === 'error' || !messageEvent.data) {
          this.sseSubscription.unsubscribe();
          return;
        }
        const data = JSON.parse(messageEvent.data);
        if (data.progress !== undefined) {
          this.progressSubject.next({ data: data.message, progress: data.progress });
        }
      });
  }

  initVertexChat(projectId: number, sessionId: number) {
    return this.http.get<any>(`${environment.api}research/chat-vertexai/${projectId}/${sessionId}`);
  }

  sendVertexMessage(projectId: number, sessionId: number, message: string) {
    return this.http.post<any>(`${environment.api}research/chat-vertexai/${projectId}/${sessionId}`, { message });
  }

  endVertexChat(projectId: number, sessionId: number) {
    return this.http.delete<any>(`${environment.api}research/chat-vertexai/${projectId}/${sessionId}`);
  }

  listResearchUsers() {
    return this.http.get<any>(`${environment.api}research/users`);
  }

  inviteResearchUser(data: any) {
    return this.http.post<any>(`${environment.api}research/users`, data);
  }

  updateResearchUser(id: number, data: any) {
    return this.http.patch<any>(`${environment.api}research/users/${id}`, data);
  }

  reInviteResearchUser(id: number) {
    return this.http.post<any>(`${environment.api}research/users/${id}/reinvite`, {});
  }

  acceptInvite() {
    return this.http.patch<any>(`${environment.api}research/accept`, {});
  }

  declineInvite() {
    return this.http.patch<any>(`${environment.api}research/decline`, {});
  }

  listMembers(projectId: number) {
    return this.http.get<any>(`${environment.api}research/${projectId}/members`);
  }

  addMember(projectId: number, data: any) {
    return this.http.post<any>(`${environment.api}research/${projectId}/members`, data);
  }

  removeMember(projectId: number, memberId: number) {
    return this.http.delete<any>(`${environment.api}research/${projectId}/members/${memberId}`);
  }
}
